import React, { useState, useEffect } from 'react';
import { PrivateAxios } from '../../../request/axios.js';
import { toast } from 'react-toastify';
import { FaSearch, FaTrashAlt, FaDownload } from 'react-icons/fa';
import Layout from '../../layout/Layout.js';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from "jwt-decode";
import ModalContrat from '../ModalContrat.js';
import Modal from 'react-modal';
import * as XLSX from "xlsx"

const availableFilters = [
    { name: 'nom', label: 'Nom', type: 'text' },
    { name: 'nom_epouse', label: "Nom d'épouse", type: 'text' },
    { name: 'prenom', label: 'Prénom', type: 'text' },
    { name: 'date_depart', label: 'Date de départ', type: 'date' },
    { name: 'date_retour', label: 'Date de retour', type: 'date' },
    { name: 'duree', label: 'Durée', type: 'number' },
    { name: 'destination', label: 'Destination', type: 'text' },
    { name: 'is_tagged', label: 'Tagué', type: 'select', options: [{ value: 'true', label: 'Oui' }, { value: 'false', label: 'Non' }] },
    { name: 'is_canceled', label: 'Annulé', type: 'select', options: [{ value: 'true', label: 'Oui' }, { value: 'false', label: 'Non' }] },
    { name: 'username', label: 'Créé par', type: 'text' },
    { name: 'created_date_min', label: 'Date de création (à partir du)', type: 'date' },
    { name: 'created_date_max', label: 'Date de création (avant le)', type: 'date' },
    { name: 'ug_nom', label: 'UG Nom', type: 'text' },
    { name: 'intermediaire_nom', label: 'Intermédiaire Nom', type: 'text' },
    { name: 'beneficiaire_nom', label: 'Bénéficiaire Nom', type: 'text' },
    { name: 'beneficiaire_prenom', label: 'Bénéficiaire Prénom', type: 'text' },
    { name: 'reference', label: 'Référence contrat', type: 'text' },
    { name: 'remise', label: 'Remise', type: 'select', options: [{ value: "true", label: 'Oui' }, { value: "false", label: 'Non' }] },
    { name: 'remise_partenaire', label: 'Remise partenaire', type: 'select', options: [{ value: "true", label: 'Oui' }, { value: "false", label: 'Non' }] },
    { name: 'remise_exceptionnelle', label: 'Remise exceptionnelle', type: 'select', options: [{ value: "true", label: 'Oui' }, { value: "false", label: 'Non' }] }

];

const RechercheContrat = () => {
    const [filters, setFilters] = useState([]);
    const [contrats, setContrats] = useState(null);
    const [selectedFilter, setSelectedFilter] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [selectedContrat, setSelectedContrat] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [userRoles, setUserRoles] = useState([]);
    const [zone, setZone] = useState('');
    const navigate = useNavigate();

    // Ajouter un filtre automatiquement lorsqu'il est sélectionné
    const handleSelectChange = (e) => {
        const selected = e.target.value;
        if (selected && !filters.some(filter => filter.name === selected)) {
            const filterToAdd = availableFilters.find(f => f.name === selected);
            setFilters([...filters, { ...filterToAdd, value: '' }]);
        }
        setSelectedFilter(''); // Réinitialiser la sélection
    };

    // Mettre à jour la valeur du filtre
    const handleFilterChange = (name, value) => {
        setFilters(filters.map(filter => filter.name === name ? { ...filter, value } : filter));
    };

    // Supprimer un filtre
    const removeFilter = (name) => {
        setFilters(filters.filter(filter => filter.name !== name));
    };

    // Requête de recherche
    const searchContrats = async () => {
        // Validation de la plage de dates de création (bornes incluses)
        const createdMin = filters.find(f => f.name === 'created_date_min')?.value;
        const createdMax = filters.find(f => f.name === 'created_date_max')?.value;
        if (createdMin && createdMax && createdMin > createdMax) {
            toast.error('La date « à partir du » doit être antérieure ou égale à la date « avant le ».');
            return;
        }
        try {
            setContrats(null);
            setIsLoading(true);
            const queryParams = filters.reduce((acc, filter) => {
                if (filter.name === 'is_tagged' && filter.value === '') return acc; // Ne pas ajouter is_tagged s'il est vide
                if ((filter.name === 'created_date_min' || filter.name === 'created_date_max') && filter.value === '') return acc; // Ne pas envoyer de plage de dates vide
                acc[filter.name] = filter.value;
                return acc;
            }, {});
            const query = new URLSearchParams(queryParams).toString();
            const response = await PrivateAxios.get(`/contrat/search?${query}`);
            if (Object.keys(response.data.contrats).length === 0) {
                toast.info('Aucun contrat trouvé');
                return;
            }
            setContrats(response.data.contrats);
            console.log(response.data.contrats);
            setZone(response.data.zone);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Erreur lors de la recherche des contrats.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = async () => {
        try {

            const dataSource = contrats;

            if (!dataSource || typeof dataSource !== "object" || Object.keys(dataSource).length === 0) {
                console.warn("Aucune donnée 'contrats' à exporter.");
                return;
            }

            const headers = [
                "reference",
                "created_date",
                "souscripteur",
                "totalHT",
                "totalTTC",
                "dateDepart",
                "intermediaire",
                "username",
            ];

            const wb = XLSX.utils.book_new();

            Object.entries(dataSource).forEach(([ugKey, intermediairesObj]) => {
                const rows = [];

                if (intermediairesObj && typeof intermediairesObj === "object") {
                    Object.values(intermediairesObj).forEach((entries) => {
                        (entries || []).forEach((item) => {
                            const d = item?.data ?? {};
                            const sous = d?.souscripteur ?? {};
                            const contrat = d?.contrat ?? {};
                            const tarif = contrat?.tarif ?? {};

                            rows.push({
                                reference: d?.reference ?? "",
                                created_date: item?.created_date ?? "",
                                souscripteur: [sous?.nomEpouse ? `${sous.nom} ep ${sous.nomEpouse}` : sous?.nom, sous?.prenom].filter(Boolean).join(" ").trim(),
                                totalHT: tarif?.totalHT ?? "",
                                totalTTC: tarif?.totalTTC ?? "",
                                dateDepart: sous?.dateDepart ?? "",
                                intermediaire: item?.intermediaire ?? "",
                                username: item?.username ?? "",
                            });
                        });
                    });
                }

                const ws = XLSX.utils.json_to_sheet(rows, { header: headers });

                // largeur de colonnes auto (approx.)
                const colWidths = headers.map((h) => {
                    const maxLen = Math.max(
                        h.length,
                        ...rows.map((r) => (r[h] !== null && r[h] !== undefined ? String(r[h]).length : 0))
                    );
                    return { wch: Math.min(Math.max(maxLen + 2, 10), 50) };
                });
                ws["!cols"] = colWidths;

                XLSX.utils.book_append_sheet(wb, ws, String(ugKey).slice(0, 31) || "Feuille");
            });

            const today = new Date().toISOString().slice(0, 10);
            XLSX.writeFile(wb, `Contrats_${today}.xlsx`);
        } catch (err) {
            console.error("Erreur lors de la génération de l'Excel :", err);
        }
    };


    const openModal = (contrat) => {
        setSelectedContrat(contrat);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setSelectedContrat(null);
        setIsModalOpen(false);
    };

    // Affichage des résultats
    const renderResults = () => {
        if (!contrats) return null;

        return (
            <div className="px-4 space-y-8 lg:px-0">
                {Object.keys(contrats).map((ug, index) => (
                    <div key={ug} className="mb-8">
                        {/* Titre UG avec les couleurs AXA alternées */}
                        <h2
                            className={`text-3xl font-semibold text-white py-2 px-4 rounded-md mb-4 ${index % 2 === 0 ? 'bg-axa-blue-400' : 'bg-sienna-400'}`}
                        >
                            UG : {ug}
                        </h2>
                        {Object.keys(contrats[ug]).map((intermediaire) => (
                            <div key={intermediaire} className="mb-8">
                                <div className="p-4 overflow-hidden bg-white rounded-lg shadow-xl">
                                    {/* Titre Intermédiaire avec couleur gris foncé */}
                                    <h3 className={`text-2xl font-semibold bg-white border-x-4 ${index % 2 === 0 ? 'border-axa-blue-400 text-axa-blue-400' : 'border-sienna-400 text-sienna-400'} py-2 px-4 rounded-md mb-4 shadow`}>
                                        Intermédiaire : {intermediaire}
                                    </h3>
                                    <table className="min-w-full overflow-hidden bg-white rounded-lg">
                                        <thead className="text-center text-gray-800 bg-gray-200">
                                            <tr>
                                                <th className="px-4 py-3">Référence</th>
                                                <th className="px-4 py-3">Nom du Souscripteur</th>
                                                <th className="px-4 py-3">Destination</th>
                                                <th className="px-4 py-3">Date de Départ</th>
                                                <th className="px-4 py-3">Date de Retour</th>
                                                <th className="px-4 py-3">Voyageurs</th>
                                                <th className="px-4 py-3">Tarif TTC (XFP)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {contrats[ug][intermediaire].map((contrat) => (
                                                <tr
                                                    key={contrat.id}
                                                    className={`border-t border-gray-200 text-center hover:bg-blue-100 hover:shadow-lg hover:scale-105 transform transition-all cursor-pointer duration-150`}
                                                    onClick={() => openModal(contrat)}
                                                >
                                                    <td className="px-4 py-2 font-medium text-gray-700">
                                                        {contrat.data.reference}
                                                    </td>
                                                    <td className="px-4 py-2 font-medium text-gray-700">
                                                        {contrat.data.souscripteur.nomEpouse ? `${contrat.data.souscripteur.nom} ep ${contrat.data.souscripteur.nomEpouse}` : contrat.data.souscripteur.nom} {contrat.data.souscripteur.prenom}
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        {contrat.data.souscripteur.destination}
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        {contrat.data.souscripteur.dateDepart}
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        {contrat.data.souscripteur.dateRetour}
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        {contrat.data.personnes.length}
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        {contrat.data.contrat.tarif.totalTTC.toLocaleString('fr-FR', {
                                                            minimumFractionDigits: 0,
                                                        })}{' '}
                                                        XPF
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        );
    };

    useEffect(() => {
        const token = localStorage.getItem('assistance-axa-token');
        if (token) {
            const decoded = jwtDecode(token);
            const currentTime = Date.now() / 1000;
            if (decoded.exp < currentTime) {
                navigate('/logout', { state: { message: 'Votre session a expiré. Veuillez vous reconnecter.' } });
            }
            setUserRoles(decoded.roles || []);
        }
    }, [navigate]);

    return (
        <Layout>
            <div className="p-6 bg-gray-100 rounded-lg shadow-md">
                <h1 className="mb-6 text-2xl font-bold text-black">Recherche de Contrats</h1>

                {/* Sélection des filtres */}
                <div className="flex items-center mb-6 space-x-4">
                    <select
                        value={selectedFilter}
                        onChange={handleSelectChange} // Ajout automatique
                        className="w-1/3 p-2 border border-gray-300 rounded-lg"
                    >
                        <option value="">Choisir un filtre...</option>
                        {availableFilters.map((filter) => (
                            <option key={filter.name} value={filter.name}>
                                {filter.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Champs des filtres ajoutés */}
                <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2 lg:grid-cols-3">
                    {filters.map((filter) => (
                        <div key={filter.name} className="flex items-center p-4 space-x-4 bg-white rounded-lg shadow-md">
                            <label className="block mb-2 font-semibold text-gray-700" htmlFor={filter.name}>
                                {filter.label} {/* Affichage du titre du filtre */}
                            </label>
                            {filter.type === 'select' ? (
                                <select
                                    value={filter.value}
                                    onChange={(e) => handleFilterChange(filter.name, e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg"
                                >
                                    <option value="">Sélectionner une option</option>
                                    {filter.options.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type={filter.type}
                                    value={filter.value}
                                    onChange={(e) => handleFilterChange(filter.name, e.target.value)}
                                    placeholder={`Entrez ${filter.label}`}
                                    className="w-full p-2 border border-gray-300 rounded-lg"
                                />
                            )}
                            <button
                                onClick={() => removeFilter(filter.name)}
                                className="text-red-500 hover:text-red-700"
                            >
                                <FaTrashAlt className="w-5 h-5" />
                            </button>
                        </div>
                    ))}
                </div>

                {/* Bouton de recherche */}
                <div className="flex justify-end mb-8">
                    <div className='mr-4'>
                        <button
                            onClick={searchContrats}
                            className="flex items-center px-4 py-2 space-x-2 text-white transition duration-200 rounded-full shadow bg-axa-blue-400 hover:bg-hover-axa-blue"
                            disabled={isLoading}
                        >
                            <FaSearch className="w-5 h-5" />
                            <span>Rechercher</span>
                        </button>
                    </div>
                    {contrats && (
                        <div>
                            <button
                                onClick={handleDownload}
                                className="flex items-center px-4 py-2 space-x-2 font-semibold text-black transition duration-200 rounded-full shadow bg-warning-yellow hover:bg-yellow-600"
                                disabled={isLoading}
                            >
                                <FaDownload className="w-5 h-5" />
                                <span>Télécharger</span>
                            </button>
                        </div>
                    )}
                </div>

                <Modal
                    isOpen={isModalOpen}
                    onRequestClose={closeModal}
                    contentLabel="Détails du Contrat"
                    className="fixed inset-0 max-w-3xl p-8 mx-auto mt-20 overflow-auto bg-white border rounded-lg shadow-lg lg:w-3/4 sm:w-full"
                    overlayClassName="fixed inset-0 bg-black bg-opacity-50"
                >
                    <ModalContrat
                    selectedContrat={selectedContrat}
                    zone={zone}
                    userRoles={userRoles}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    navigate={navigate}
                    closeModal={closeModal}
                />
                </Modal>

                {/* Résultats de la recherche */}
                {isLoading ? <p>Chargement...</p> : renderResults()}
            </div>
        </Layout>
    );
};

export default RechercheContrat;
